import { z } from 'zod';

import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import {
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRendererProps,
    toRecord,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const IMAGE_MIME_TYPES = [
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/webp',
] as const;

const imagePayloadSchema = z.object({
    mime_type: z.enum(IMAGE_MIME_TYPES),
    size_bytes: z.number().int().nonnegative().max(MAX_IMAGE_BYTES),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    filename: z.string().min(1).nullable(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
});

export function AdminImageArtifact({ artifact }: AdminArtifactRendererProps) {
    const parsed = imagePayloadSchema.safeParse(toRecord(artifact.payload));
    if (!parsed.success) {
        return (
            <ArtifactFrame
                artifact={artifact}
                status={
                    <ArtifactLifecycleStatusBadge
                        lifecycleStatus={artifact.lifecycle_status}
                    />
                }
            >
                <ArtifactEvidence artifact={artifact} />
                <p className="text-muted-foreground text-xs">
                    This image artifact does not match the admin.image@1
                    contract, so the admin UI cannot render it safely.
                </p>
            </ArtifactFrame>
        );
    }

    const payload = parsed.data;
    const { apiUrl } = getRuntimeConfig();
    const imageUrl = `${apiUrl}/admin/agent/runs/${artifact.phoebe_agent_run_id}/artifacts/${artifact.id}/image`;
    const label = payload.filename ?? 'Sandbox analysis image';

    return (
        <ArtifactFrame
            artifact={artifact}
            title={label}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            <div className="grid grid-cols-3 gap-x-4 gap-y-2 border-t border-[var(--aa-border,#e8e6e1)] pt-2.5 text-xs">
                <ImageMetadata label="Format" value={payload.mime_type} />
                <ImageMetadata
                    label="Dimensions"
                    value={formatImageDimensions(payload.width, payload.height)}
                />
                <ImageMetadata
                    label="Size"
                    value={formatImageSize(payload.size_bytes)}
                />
            </div>
            <a
                href={imageUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="admin-image-artifact-link"
                className="group/image block overflow-hidden rounded-[6px] border border-[var(--aa-border,#e8e6e1)] bg-[var(--aa-surface,#f7f7f5)]"
            >
                <img
                    src={imageUrl}
                    alt={label}
                    data-testid="admin-image-artifact"
                    className="h-auto w-full object-contain transition-opacity group-hover/image:opacity-90"
                    loading="lazy"
                />
            </a>
        </ArtifactFrame>
    );
}

function ImageMetadata({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string;
}) {
    return (
        <div>
            <div className="text-muted-foreground/70 text-[11px]">{label}</div>
            <div className="text-foreground mt-0.5 font-medium break-all">
                {value}
            </div>
        </div>
    );
}

function formatImageSize(sizeBytes: number): string {
    if (sizeBytes < 1024) {
        return `${sizeBytes} B`;
    }
    return `${(sizeBytes / 1024).toFixed(1)} KB`;
}

function formatImageDimensions(
    width: number | null,
    height: number | null,
): string {
    if (width === null || height === null) {
        return 'Unknown';
    }
    return `${width} × ${height}`;
}
